import { UseMutationOptions, useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { FormStateValues } from '@components/form';
import { useToaster } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

import { useModal } from 'features/modals';

import {
  reykjavikForm,
  reykjavikPreviewDocument,
  SubmitSuccessResponse,
  SubmitError,
  SubmitDocumentForm,
  reykjavikSubmitDocument,
  reykjavikExternalListBanks,
  reykjavikExternalListPostCodes,
  reykjavikExternalListCostLocations,
  reykjavikExternalListJobTitles,
  reykjavikExternalListUnions,
  reykjavikExternalListJob,
  reykjavikDeleteDocument,
  reykjavikExternalListWorkplaces,
  reykjavikExternalListPensions,
  createJobCase,
  getJobCase,
  getSapApplication,
  createSapApplication,
  JobCaseType,
} from './api';
import locale from './locale.json';

export const useReykjavikForm = (applicationId: string) => {
  return useQuery({
    queryKey: ['reykjavik-onboarding-form', applicationId],
    queryFn: () => reykjavikForm(applicationId),
  });
};

export const usePreviewDocument = (applicationId: string, templateId: string) => {
  const { showFullscreenModal } = useModal();

  return useMutation(
    (data: FormStateValues) => reykjavikPreviewDocument(applicationId, templateId, data),
    {
      mutationKey: ['reykjavik-preview-document'],
      onSuccess: (data) => {
        showFullscreenModal('CUSTOM_FILE_PREVIEW', { filePath: data.url });
      },
    },
  );
};

export const useSubmitReykjavikDocument = (applicationId: string, templateId: string) => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess, showError } = useToaster();
  const client = useQueryClient();

  return useMutation<SubmitSuccessResponse, SubmitError, SubmitDocumentForm>({
    mutationKey: ['reykjavik-submit-document'],
    mutationFn: (data) => {
      return reykjavikSubmitDocument(applicationId, templateId, {
        ...data,
        cosigners: data.cosigners,
      });
    },
    onSuccess: () => {
      showSuccess(t('sentToSigning'));
      client.resetQueries(['reykjavik-onboarding-form', applicationId]);
      client.invalidateQueries(['application-onboarding-load', applicationId]);
    },
    onError: () => {
      showError(t('errorOccurred'));
    },
  });
};

export const useDeleteReykjavikDocument = (
  applicationId: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess, showError } = useToaster();
  const client = useQueryClient();

  return useMutation({
    mutationFn: reykjavikDeleteDocument,
    ...options,
    onSuccess(...data) {
      showSuccess(t('contractDeleted'));
      client.resetQueries(['reykjavik-onboarding-form', applicationId]);
      client.invalidateQueries(['application-onboarding-load', applicationId]);
      options?.onSuccess?.(...data);
    },
    onError: () => {
      showError(t('errorOccurred'));
    },
  });
};

export const useReykjavikExternalListBanks = () => {
  return useQuery({
    queryKey: ['reykjavik-external-list-banks'],
    queryFn: () => reykjavikExternalListBanks(),
  });
};

export const useReykjavikExternalListPostCodes = () => {
  return useQuery({
    queryKey: ['reykjavik-external-list-postcodes'],
    queryFn: () => reykjavikExternalListPostCodes(),
  });
};

export const useReykjavikExternalListCostLocations = (id: string) => {
  return useQuery({
    queryKey: ['reykjavik-external-list-costlocation', id],
    queryFn: () => reykjavikExternalListCostLocations(id),
    enabled: Boolean(id),
  });
};

export const useReykjavikExternalListJobTitles = (id: string) => {
  return useQuery({
    queryKey: ['reykjavik-external-list-jobtitles', id],
    queryFn: () => reykjavikExternalListJobTitles(id),
    enabled: Boolean(id),
  });
};

export const useReykjavikExternalListUnions = (id: string) => {
  return useQuery({
    queryKey: ['reykjavik-external-list-unions', id],
    queryFn: () => reykjavikExternalListUnions(id),
    enabled: Boolean(id),
  });
};

export const useReykjavikExternalListJob = (id: string) => {
  return useQuery({
    queryKey: ['reykjavik-external-list-job', id],
    queryFn: () => reykjavikExternalListJob(id),
    enabled: Boolean(id),
  });
};

export const useReykjavikExternalListWorkplace = (id: string) => {
  return useQuery({
    queryKey: ['reykjavik-external-list-workplace', id],
    queryFn: () => reykjavikExternalListWorkplaces(id),
    enabled: Boolean(id),
  });
};

export const useReykjavikExternalListPensions = (id: string) => {
  return useQuery({
    queryKey: ['reykjavik-external-list-pension', id],
    queryFn: () => reykjavikExternalListPensions(id),
    enabled: Boolean(id),
  });
};

export const useHladanJobCase = (
  jobId: string,
  refetch?: boolean,
  onSuccess?: (data: JobCaseType) => void,
) => {
  return useQuery({
    queryKey: ['hladan-job-case', jobId],
    queryFn: () => getJobCase(jobId),
    refetchInterval: refetch ? 5000 : false,
    onSuccess(data) {
      onSuccess?.(data);
    },
  });
};

export const useCreateJobCase = (jobId: string) => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess, showError } = useToaster();

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => createJobCase(jobId),
    onSuccess: (...data) => {
      queryClient.invalidateQueries(['hladan-job-case', jobId]);
      showSuccess(t('dataWillBeSent'));
    },
    onError: (data) => {
      showError(t('errorOccured'));
    },
  });
};

const getSapApplicationQueryKey = (applicationId: string) => ['rvk-sap-application', applicationId];

export const useInvalidateSap = () => {
  const queryClient = useQueryClient();
  const invalidateApplication = (applicationId: string) =>
    queryClient.invalidateQueries(getSapApplicationQueryKey(applicationId));
  return { invalidateApplication };
};

export const useSapApplication = (applicationId: string) => {
  return useQuery({
    queryKey: getSapApplicationQueryKey(applicationId),
    queryFn: () => getSapApplication(applicationId),
  });
};

export const useCreateSapApplication = (applicationId: string) => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess, showError } = useToaster();
  const { invalidateApplication } = useInvalidateSap();

  return useMutation({
    mutationFn: () => createSapApplication(applicationId),
    onSuccess: (...data) => {
      invalidateApplication(applicationId);
      showSuccess(t('sapCreated'));
    },
    onError: (error: AxiosError, variables, context) => {
      if (error.response?.status) {
        showError(t('sapErrorWithCode', { status: error.response.status }));
      } else {
        showError(t('errorOccured'));
      }
    },
  });
};
